import {
  DatePipe,
  DecimalPipe,
  NgClass,
  NgTemplateOutlet,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics.service';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { AuctionService } from '@core/services/auction.service';
import { AuthService } from '@core/services/auth.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faCalendarAlt,
  faHeart as faHeartRegular,
} from '@fortawesome/free-regular-svg-icons';
import {
  faFlag,
  faHeart,
  faHeart as faHeartSolid,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { BidService } from '@shared/services/bid.service';
import {
  CountdownService,
  CountdownViewModel,
} from '@shared/services/countdown.service';
import { EventService } from '@shared/services/event.service';
import { LanguageService } from '@shared/services/language.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { Subscription, interval } from 'rxjs';
import { AuctionView } from 'src/app/articles-manager/interfaces/auction.interface';
import { ArticleService } from 'src/app/articles-manager/services/article.service';
import { TableChallengeDialogComponent } from 'src/app/event-now/components/table-challenge-dialog/table-challenge-dialog.component';
import { SellerBrandingService } from 'src/app/seller/services/seller-branding.service';

const memorabidAuctionTypeDescription = 'Memorabid';

@Component({
    selector: 'app-bid-element',
    templateUrl: './bid-element.component.html',
    styleUrls: ['./bid-element.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        FontAwesomeModule,
        RouterModule,
        NgClass,
        NgTemplateOutlet,
        DecimalPipe,
    ]
})
@UntilDestroy()
export class BidElementComponent implements OnInit, OnDestroy {
  @Input() idEvent: number = -1;
  slug = input<string | null>(null);
  auction = input<AuctionView>();
  dark = input<boolean>(false);
  minimal = input<boolean>(false);
  eagerImage = input<boolean>(false);
  highPriorityImage = input<boolean>(false);

  faHeartRegular = faHeartRegular;
  faHeart = faHeart;
  faHeartSolid = faHeartSolid;
  faFlag = faFlag;
  faCalendarAlt = faCalendarAlt;
  faStar = faStar;

  isMemorabid: boolean = false;
  subscription: Subscription;
  timeDifference = signal<number>(0);
  language: string;

  // Card widths range roughly 2-7 grid columns (or a near-full-width carousel
  // slot on mobile) — srcset/sizes let the browser pick the right one itself,
  // before it ever requests an image, instead of measuring the rendered DOM
  // width after first paint and swapping `src` to match (which was visibly
  // re-fetching and flashing every single card).
  readonly articleImageWidths = [300, 450, 600, 800, 1000];
  readonly articleImageSizes =
    '(max-width: 576px) 90vw, (max-width: 992px) 45vw, (max-width: 1200px) 30vw, (max-width: 1400px) 20vw, 16vw';

  isBranded = computed(() => {
    return this.brandingService.isBranded();
  });

  isPreEvent = computed(() => {
    return (
      (this.auction()?.isPreEvent &&
        new Date(this.auction().preEventCloseDate) > new Date()) ||
      false
    );
  });

  brandingService = inject(SellerBrandingService);

  public timeDifferenceFromStart: number;
  countdown = signal<CountdownViewModel | null>(null);
  hasActiveCountdown = computed(() => !!this.countdown()?.isActive);
  hasExpiredCountdown = computed(() => !!this.countdown()?.hasExpired);
  countdownIsExpiring = computed(() => !!this.countdown()?.isExpiring);
  countdownPrimaryPart = computed(() => this.countdown()?.parts[0]);
  countdownSecondaryPart = computed(() => this.countdown()?.parts[1]);
  countdownTertiaryPart = computed(() => {
    const part = this.countdown()?.parts[2];
    return part?.labelKey ? part : undefined;
  });
  countdownValueClass = computed(() =>
    this.countdown()?.mode === 'days-hours' ? 'timeGreen' : 'timeRed'
  );
  showExpiringBadge = computed(() => {
    return (
      this.idEvent === -1 &&
      !this.auction()?.buyItNow &&
      this.timeDifferenceFromStart > 0 &&
      this.countdownIsExpiring()
    );
  });

  articleImageLoadingMode = computed<'lazy' | 'eager'>(() =>
    this.eagerImage() ? 'eager' : 'lazy'
  );
  // An eager image outside the priority tier is below-the-fold bulk: still
  // fetched right away so it is already cached by the time it scrolls into
  // view, but explicitly deprioritized so it cannot starve the LCP image.
  articleImageFetchPriority = computed<'high' | 'low' | 'auto'>(() => {
    if (this.highPriorityImage()) {
      return 'high';
    }
    return this.eagerImage() ? 'low' : 'auto';
  });

  @ViewChild('bidHeader') bidHeaderRef: ElementRef<HTMLElement>;
  isStarred = signal(false);
  underReserve = computed(() => {
    if (!this.auction()) {
      return false;
    }

    if (this.auction().hasReservePrice === false) {
      return false;
    }

    // 48 hours after the auction end date
    if (
      -this.timeDifference() > 1000 * 60 * 60 * 24 * 2 ||
      -this.timeDifference() < 0
    ) {
      return false;
    }

    return !this.auction().reservePriceReached;
  });

  showCoverReserveButton = computed(() => {
    return (
      this.slug() == null &&
      this.underReserve() &&
      this.auction()?.isUserBestBider &&
      this.authService.isAuthenticated()
    );
  });

  showProposal = computed(() => {
    const userWinner =
      this.auction().isUserBestBider && this.auction().reservePriceReached;
    if (
      !userWinner &&
      !this.underReserve() &&
      this.auction()?.isFinished &&
      !this.auction()?.buyItNow &&
      this.slug() == null &&
      this.auction()?.canDirectPropose !== false
    ) {
      return true;
    }
    return false;
  });

  private readonly articleService = inject(ArticleService);
  private readonly bidService = inject(BidService);
  public readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly datePipe = inject(DatePipe);
  private readonly auctionDownloadedService = inject(AuctionDownloadedService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly _languageService = inject(LanguageService);
  private readonly platformService = inject(PlatformService);
  private readonly auctionService = inject(AuctionService);
  private readonly countdownService = inject(CountdownService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly eventService = inject(EventService);
  private readonly modalUtils = inject(ModalUtilsService);

  constructor() {
    this.language = this._languageService.userSelectedLanguage.code;
  }

  ngOnInit(): void {
    this.isMemorabid =
      this.auction().auctionTypeDescription === memorabidAuctionTypeDescription;
    this.updateTimeDifference();
    this.platformService.runOnBrowser(() => {
      // One of these ticks per rendered card, for as long as it's on screen
      // — kept outside Angular's zone so NgZone's pending-macrotask count
      // doesn't stay permanently true (which blocks ApplicationRef.whenStable()
      // and trips NG0506 on hydration).
      this.ngZone.runOutsideAngular(() => {
        this.subscription = interval(1000).subscribe(() => {
          this.ngZone.run(() => {
            this.updateTimeDifference();
            this.cdr.markForCheck();
          });
        });
      });
    });
    this.articleService
      .isFavorite(this.auction().idArticle)
      .pipe(untilDestroyed(this))
      .subscribe((isFavorite) => {
        this.isStarred.set(isFavorite);
      });
  }

  getArticleImageSrcset(basePath: string): string {
    return this.articleImageWidths
      .map((width) => `${basePath}?w=${width} ${width}w`)
      .join(', ');
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  updateTimeDifference() {
    const startDate = this.countdownService.parseAuctionDate(
      this.auction().startDateView,
      this.datePipe
    );
    if (!startDate) {
      return;
    }

    this.timeDifferenceFromStart = Date.now() - startDate.getTime();
    const targetDate = this.countdownService.parseAuctionDate(
      this.timeDifferenceFromStart > 0
        ? this.auction().endDateView
        : this.auction().startDateView,
      this.datePipe
    );
    if (!targetDate) {
      return;
    }

    const countdown = this.countdownService.buildCountdown(targetDate);
    this.timeDifference.set(countdown.remainingMs);
    this.countdown.set(countdown);
  }

  goToArticle() {
    const backScrollPaths = [
      '/search-result-all',
      '/search-result-worn',
      '/search-result-store',
      '/search-result-signed',
      '/search-result',
      '/eventnow',
      '/home',
      '/tags',
      '/seller',
      '/promoter',
      '/campaign',
    ];

    const toScroll = backScrollPaths.some((path) =>
      this.router.url.includes(path)
    );

    if (toScroll) {
      this.auctionDownloadedService.auctionSource = this.router.url
        .split('?')[0]
        .split('/')
        .filter(Boolean)
        .slice(1)
        .join('/');
    }
    this.analyticsService.trackViewItem(this.auction());

    // Preload full auction detail in background for faster article page load
    const idAuction = this.auction()?.idAuction;
    if (idAuction) {
      // Avoid duplicate request if already cached
      if (!this.auctionDownloadedService.getPreloadedAuction(idAuction)) {
        this.auctionService
          .getByPk(idAuction)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: (fullAuction) => {
              if (fullAuction) {
                // Store in cache for detail component to pick up
                // Cast to AuctionView shape if needed (detail returns Auction)
                // Convert minimal needed fields to AuctionView shape if response is Auction (has superset of fields)
                const toCache: AuctionView = {
                  ...(fullAuction as unknown as AuctionView),
                };
                this.auctionDownloadedService.cacheAuctionDetail(toCache);
              }
            },
            error: () => {
              // Silently ignore preload errors; navigation still proceeds
            },
          });
      }
    }
  }

  onToggleWishlist(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.auction()?.idArticle) {
      const starredSub = this.isStarred()
        ? this.articleService.delete({ idArticle: this.auction().idArticle })
        : this.articleService.save({ idArticle: this.auction().idArticle });
      starredSub.subscribe();
    }
  }

  openProposalPopup(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.auction()?.idAuction) {
      this.ensureTableAssignmentThenDirectOffer(this.auction().idAuction);
    }
  }

  private ensureTableAssignmentThenDirectOffer(idAuction: number): void {
    // If not authenticated, proceed directly with offer
    if (!this.authService.isAuthenticated()) {
      this.bidService.makeOfferMessage(idAuction);
      return;
    }

    // If idEvent is not set, proceed directly with offer
    if (this.idEvent === -1) {
      this.bidService.makeOfferMessage(idAuction);
      return;
    }

    // Fetch event by id to check if table challenge is enabled
    this.eventService
      .getAllViewByPk(this.idEvent, this._languageService.userSelectedLanguage.idLanguage)
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (!event?.enableTableChallenge) {
          // Table challenge not enabled, proceed with offer
          this.bidService.makeOfferMessage(idAuction);
          return;
        }

        // Table challenge enabled, check if user is assigned
        this.eventService
          .getMyTableChallengeAssignment(this.idEvent)
          .pipe(untilDestroyed(this))
          .subscribe((assignment) => {
            if (assignment?.tableName) {
              // User is already assigned, proceed with offer
              this.bidService.makeOfferMessage(idAuction);
            } else {
              // User is not assigned, show table challenge dialog first
              const tableOptions = event.tableChallengeTables ?? [];
              if (!tableOptions.length) {
                // No tables available, proceed anyway
                this.bidService.makeOfferMessage(idAuction);
                return;
              }

              const ref = this.modalUtils.openModal(TableChallengeDialogComponent, {
                centered: true,
                backdrop: 'static',
                keyboard: false,
              });

              ref.componentInstance.idEvent = this.idEvent;
              ref.componentInstance.tableOptions = tableOptions;

              ref.result.then(
                () => {
                  // Assignment successful, proceed with offer
                  this.bidService.makeOfferMessage(idAuction);
                },
                () => {
                  // Dialog dismissed, do nothing
                }
              );
            }
          });
      });
  }

  openCoverReservePopup(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.auction()?.idAuction) {
      this.ensureTableAssignmentThenBid(this.auction().idAuction);
    }
  }

  private ensureTableAssignmentThenBid(idAuction: number): void {
    // If not authenticated, proceed directly with bid
    if (!this.authService.isAuthenticated()) {
      this.bidService.makeBidReservePrice(idAuction);
      return;
    }

    // If idEvent is not set, proceed directly with bid
    if (this.idEvent === -1) {
      this.bidService.makeBidReservePrice(idAuction);
      return;
    }

    // Fetch event by id to check if table challenge is enabled
    this.eventService
      .getAllViewByPk(this.idEvent, this._languageService.userSelectedLanguage.idLanguage)
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (!event?.enableTableChallenge) {
          // Table challenge not enabled, proceed with bid
          this.bidService.makeBidReservePrice(idAuction);
          return;
        }

        // Table challenge enabled, check if user is assigned
        this.eventService
          .getMyTableChallengeAssignment(this.idEvent)
          .pipe(untilDestroyed(this))
          .subscribe((assignment) => {
            if (assignment?.tableName) {
              // User is already assigned, proceed with bid
              this.bidService.makeBidReservePrice(idAuction);
            } else {
              // User is not assigned, show table challenge dialog first
              const tableOptions = event.tableChallengeTables ?? [];
              if (!tableOptions.length) {
                // No tables available, proceed anyway
                this.bidService.makeBidReservePrice(idAuction);
                return;
              }

              const ref = this.modalUtils.openModal(TableChallengeDialogComponent, {
                centered: true,
                backdrop: 'static',
                keyboard: false,
              });

              ref.componentInstance.idEvent = this.idEvent;
              ref.componentInstance.tableOptions = tableOptions;

              ref.result.then(
                () => {
                  // Assignment successful, proceed with bid
                  this.bidService.makeBidReservePrice(idAuction);
                },
                () => {
                  // Dialog dismissed, do nothing
                }
              );
            }
          });
      });
  }

  getCardActionLabel(): string {
    const currentAuction = this.auction();
    if (!currentAuction?.buyItNow) {
      return 'IDS_EVENT_AUCTION_BASE';
    }

    if (currentAuction.isDonationPackage) {
      return 'IDS_DONATION';
    }

    return 'IDS_BUY_NOW';
  }

  onCardMouseEnter(cardEl: HTMLElement): void {
    const header = this.bidHeaderRef?.nativeElement;
    if (header) {
      // Temporarily expand to measure actual content height
      header.style.maxHeight = 'none';
      header.style.padding = `${5}px`;
      const height = header.offsetHeight + 5;
      header.style.maxHeight = '';
      header.style.padding = '';
      cardEl.style.setProperty('--header-height', `${height}px`);
    }
  }

  onCardMouseLeave(cardEl: HTMLElement): void {
    cardEl.style.removeProperty('--header-height');
  }
}
